import { useEffect, useState } from "react";

type Props = {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  ariaLabel?: string;
  /** Total width of the control. */
  width?: number;
};

/**
 * Practical numeric stepper: [−] value [+]. Replaces native number-input
 * spinners (tiny, ugly, and they hijack the mouse wheel). The middle field is
 * still free-type editable; hold-to-repeat is intentionally omitted — line
 * quantities are small. Use for any ±step field (quantity, discount).
 */
export function QtyStepper({
  value, onChange, min = 0, max, step = 1, ariaLabel, width = 116,
}: Props) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);

  const clamp = (n: number) => {
    let v = n;
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    // Kill float drift from fractional steps (e.g. 0.5 discount).
    return Math.round(v * 1000) / 1000;
  };
  const commit = (n: number) => { const v = clamp(n); onChange(v); setDraft(String(v)); };

  const atMin = min != null && value <= min;
  const atMax = max != null && value >= max;

  return (
    <div className="qty-stepper" style={{ width }}>
      <button
        type="button" className="qty-stepper-btn" aria-label="−"
        disabled={atMin} onClick={() => commit(value - step)}
      >
        <svg viewBox="0 0 24 24" aria-hidden><path d="M5 12h14" /></svg>
      </button>
      <input
        className="qty-stepper-val"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={draft}
        onChange={(e) => {
          const raw = e.target.value.replace(",", ".");
          setDraft(raw);
          const n = Number(raw);
          if (raw !== "" && !Number.isNaN(n)) onChange(clamp(n));
        }}
        onBlur={() => commit(Number(draft.replace(",", ".")) || min || 0)}
      />
      <button
        type="button" className="qty-stepper-btn" aria-label="+"
        disabled={atMax} onClick={() => commit(value + step)}
      >
        <svg viewBox="0 0 24 24" aria-hidden><path d="M12 5v14M5 12h14" /></svg>
      </button>
    </div>
  );
}
